import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Checkbox from 'components/global/Checkbox';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import axios from 'services/axios';
import { CUSTOM_ROLE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const RoleTemplatesModal = NiceModal.create(({ id, modalName }) => {
	const isUpdateMode = !!id;
	const [isMinimizing, setIsMinimizing] = useState(false);

	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);

	const { closeModal } = useModalRouter();

	const handleCloseModal = () => {
		closeModal();
	};

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().required(),
					roleTreeIDs: yup.array().min(1).nullable().required()
				})
				.required(),
		[]
	);

	const fillForm = useCallback(async () => {
		const defaultValues = {
			name: null,
			roleTreeIDs: []
		};
		if (!isUpdateMode) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(CUSTOM_ROLE.GET_ROLE_TEMPLATE, {
				ID: id
			});

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}

			return {
				...defaultValues,
				...data,
				name: data?.templateName ?? null
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		setValue,
		watch,
		getValues,
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	const handleCheckboxChange = useCallback((row) => {
		let roleTreeIDs = getValues('roleTreeIDs');

		const leafRows = row.getLeafRows();
		const parentRows = row.getParentRows();
		const allIds = [row.original.id, ...leafRows.map((a) => a.original.id)];

		if (roleTreeIDs.includes(row.original.id)) {
			roleTreeIDs = roleTreeIDs.filter((item) => !allIds.includes(item));
		} else {
			roleTreeIDs = Array.from(new Set([...roleTreeIDs, ...allIds, ...parentRows.map((a) => a.original.id)]));
			if (row.getCanExpand() && !row.getIsExpanded()) {
				row.toggleExpanded();
				leafRows.forEach((leafRow) => {
					if (leafRow.getCanExpand() && !leafRow.getIsExpanded()) leafRow.toggleExpanded();
				});
			}
		}

		setValue('roleTreeIDs', roleTreeIDs);
	}, []);

	const columns = useMemo(
		() => [
			{
				header: 'name',
				accessorKey: 'langResource',
				size: 150,
				showOnMobile: true
			},
			{
				header: 'type',
				renderCell: ({ row }) => <Status value={row.original.type} />,
				size: 50,
				maxSize: 50,
				showOnMobile: true
			},
			{
				id: 'select',
				header: 'permission',
				renderCell: ({ row }) => (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
						onClick={() => {
							handleCheckboxChange(row);
						}}>
						<Checkbox checked={watch('roleTreeIDs')?.includes(row.original.id)} readOnly />
					</div>
				),
				size: 50,
				maxSize: 50,
				showOnMobile: true
			}
		],
		[]
	);

	const onSubmit = useCallback(async (data) => {
		setLoading(true);
		await axiosPrivate
			.post(isUpdateMode ? CUSTOM_ROLE.UPDATE_ROLE_TEMPLATE : CUSTOM_ROLE.ADD_ROLE_TEMPLATE, {
				ID: id,
				...data
			})
			.then(() => {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.ROLE_TEMPLATES));
				handleCloseModal();
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: error.response?.data.code || error.response?.data.message
				});
			});
		setLoading(false);
	}, []);

	const defaultColSpan = {
		md: 12
	};

	const [data, setData] = useState([]);

	const convertDataToTreeData = useCallback((arr) => {
		const { res } = arr.reduce(
			(acc, curr) => {
				if (acc.parentMap[curr.parentID]) {
					if (!acc.parentMap[curr.parentID].children) {
						acc.parentMap[curr.parentID].children = [];
					}
					acc.parentMap[curr.parentID].children.push(curr);
				} else {
					acc.res.push(curr);
				}
				acc.parentMap[curr.id] = curr;
				return acc;
			},
			{ parentMap: {}, res: [] }
		);

		return res;
	}, []);

	const fetchData = useCallback(async () => {
		setLoading(true);
		await axios
			.post(CUSTOM_ROLE.GET_CLIENT_ROLE_TREE_LIST, {
				paging: { offset: 0, count: 1000 },
				totalRecord: false
			})
			.then((response) => {
				const { data, status } = response;

				if (status !== 200) {
					AppLogger.error(response);
					showError();
				} else {
					setData(convertDataToTreeData(data.data));
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: error.response?.data.code || error.response?.data.message
				});
			});
		setLoading(false);
	}, []);

	useEffect(() => {
		fetchData();
	}, []);

	const [searchValue, setSearchValue] = useState(null);

	const filter = useCallback((arr, key) => {
		const getNodes = (result, object) => {
			if (object.langResource.toLowerCase().includes(key.toLowerCase())) {
				result.push(object);
				return result;
			}
			if (Array.isArray(object.children)) {
				const nodes = object.children.reduce(getNodes, []);
				if (nodes.length) result.push({ ...object, children: nodes });
			}
			return result;
		};

		return arr.reduce(getNodes, []);
	}, []);

	const filteredData = useMemo(() => {
		if (searchValue) {
			return filter(data, searchValue);
		} else {
			return data;
		}
	}, [searchValue, data]);

	return (
		<Modal
			maxWidth="medium"
			name={modalName}
			fullwidth
			className="modal-roleTemplatesModal"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={isUpdateMode ? 'updateRoleTemplates' : 'addRoleTemplates'}
					tooltip={isUpdateMode ? watch('name') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{isUpdateMode ? 'updateRoleTemplates' : 'addRoleTemplates'}</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="name" required>
						<StringInput name="name" />
					</FormItem>
					<FormItem colSpan={defaultColSpan}>
						<SearchInput fullWidth value={searchValue} setValue={setSearchValue} />
					</FormItem>
					<FormItem colSpan={defaultColSpan}>
						<Table
							columns={columns}
							data={filteredData}
							isTree={!loading}
							enableRowSelection={true}
							hideRowSelectionColumn={true}
							loading={loading}
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button variant="outline" label="cancel" onClick={handleCloseModal} />
				<Button
					label={isUpdateMode ? 'save' : 'add'}
					onClick={handleSubmit(onSubmit, (errors) => {
						if (getValues('roleTreeIDs')?.length < 1) {
							showError({
								message: 'errorMsg.pleaseSelectAtLeastOnePermission'
							});
						}
						AppLogger.log(errors);
					})}
					loading={loading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default RoleTemplatesModal;
